import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Users, Clock, Plus } from 'lucide-react';
import { toast } from 'sonner';

const Schedule = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  
  // 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState('single'); // 'single' | 'batch'
  const [creating, setCreating] = useState(false);
  
  // 단일 수업 폼
  const [singleForm, setSingleForm] = useState({
    name: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    max_capacity: 10,
    instructor: '',
    description: '',
    session_type: 'general',
  });
  
  // 반복 수업 폼
  // Helper: 시작 시간 → 종료 시간 자동 계산 (50분)
  const calcEndTime = (startTime) => {
    if (!startTime) return '';
    const [h, m] = startTime.split(':').map(Number);
    const total = h * 60 + m + 50;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const [singleEndManual, setSingleEndManual] = useState(false);
  const [batchTimeSlots, setBatchTimeSlots] = useState([{ start_time: '10:00', end_time: '10:50', endManual: false }]);

  // 반복 수업 폼
  const [batchForm, setBatchForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    days_of_week: [],
    start_time: '',
    end_time: '',
    max_capacity: 10,
    instructor: '',
    description: '',
    session_type: 'general',
  });
  
  // 반복 수업 미리보기
  const [batchPreview, setBatchPreview] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, [currentDate]);

  const fetchSessions = async () => {
    try {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 7);

      const response = await apiClient.get('/admin/class-sessions', {
        params: {
          start_date: startOfWeek.toISOString(),
          end_date: endOfWeek.toISOString()
        }
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('스케줄 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getSessionsForDay = (day) => {
    return sessions.filter(s => {
      const sessionDate = new Date(s.scheduled_at);
      return sessionDate.toDateString() === day.toDateString();
    }).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  };

  // 모달 열기
  const openCreateModal = () => {
    setShowCreateModal(true);
    setCreateMode('single');
    setBatchPreview(null);
    // 기본 날짜 설정
    const today = new Date().toISOString().split('T')[0];
    setSingleForm({
      name: '',
      scheduled_date: today,
      start_time: '10:00',
      end_time: '11:00',
      max_capacity: 10,
      instructor: '',
      description: '',
      session_type: 'general',
    });
    setBatchForm({
      name: '',
      start_date: today,
      end_date: '',
      days_of_week: [],
      start_time: '10:00',
      end_time: '11:00',
      max_capacity: 10,
      instructor: '',
      description: '',
      session_type: 'general',
    });
    setBatchTimeSlots([{ start_time: '10:00', end_time: '10:50', endManual: false }]);
    setSingleEndManual(false);
  };

  // 요일 토글
  const toggleDayOfWeek = (day) => {
    setBatchForm(prev => {
      const newDays = prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day];
      return { ...prev, days_of_week: newDays };
    });
    setBatchPreview(null);
  };

  // 반복 수업 미리보기
  const previewBatchSessions = async () => {
    if (!batchForm.name || !batchForm.start_date || !batchForm.end_date || 
        batchForm.days_of_week.length === 0 || batchTimeSlots.length === 0) {
      toast.error('필수 항목을 모두 입력해주세요');
      return;
    }
    
    try {
      const payload = {
        ...batchForm,
        time_slots: batchTimeSlots.map(s => ({ start_time: s.start_time, end_time: s.end_time }))
      };
      const response = await apiClient.post('/admin/class-sessions/preview-batch', payload);
      setBatchPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || '미리보기 실패');
    }
  };

  // 단일 수업 생성
  const createSingleSession = async () => {
    if (!singleForm.name || !singleForm.scheduled_date || !singleForm.start_time || 
        !singleForm.end_time || !singleForm.max_capacity) {
      toast.error('필수 항목을 모두 입력해주세요');
      return;
    }
    
    setCreating(true);
    try {
      await apiClient.post('/admin/class-sessions/direct', singleForm);
      toast.success('수업이 생성되었습니다');
      setShowCreateModal(false);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || '수업 생성 실패');
    } finally {
      setCreating(false);
    }
  };

  // 반복 수업 생성
  const createBatchSessions = async () => {
    if (!batchForm.name || !batchForm.start_date || !batchForm.end_date || 
        batchForm.days_of_week.length === 0 || batchTimeSlots.length === 0 || !batchForm.max_capacity) {
      toast.error('필수 항목을 모두 입력해주세요');
      return;
    }

    // Validate each time slot
    for (let i = 0; i < batchTimeSlots.length; i++) {
      const s = batchTimeSlots[i];
      if (s.start_time >= s.end_time) {
        toast.error(`시간 슬롯 ${i + 1}: 종료 시간이 시작 시간보다 늦어야 합니다`);
        return;
      }
    }
    
    setCreating(true);
    try {
      const payload = {
        ...batchForm,
        time_slots: batchTimeSlots.map(s => ({ start_time: s.start_time, end_time: s.end_time }))
      };
      const response = await apiClient.post('/admin/class-sessions/batch', payload);
      toast.success(response.data.message);
      if (response.data.skipped_duplicates > 0) {
        toast.info(`${response.data.skipped_duplicates}개의 중복 수업이 건너뛰어졌습니다`);
      }
      setShowCreateModal(false);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || '수업 생성 실패');
    } finally {
      setCreating(false);
    }
  };

  const weekDays = getWeekDays();
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">주간 스케줄</h1>
        <div className="flex gap-2">
          <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700" data-testid="create-session-btn">
            <Plus className="h-4 w-4 mr-2" />
            수업 생성
          </Button>
          <Button variant="outline" onClick={goToPreviousWeek}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={goToToday}>
            오늘
          </Button>
          <Button variant="outline" onClick={goToNextWeek}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {weekDays[0].toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })} ~ {' '}
            {weekDays[6].toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day, index) => {
              const daySessions = getSessionsForDay(day);
              const isToday = day.toDateString() === new Date().toDateString();
              
              return (
                <div key={index} className={`border rounded-lg p-3 min-h-[200px] ${isToday ? 'border-blue-500 bg-blue-50' : ''}`}>
                  <div className="text-center mb-3">
                    <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-600'}`}>
                      {dayNames[index]}
                    </div>
                    <div className={`text-lg font-bold ${isToday ? 'text-blue-600' : ''}`}>
                      {day.getDate()}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {daySessions.map((session) => {
                      const isCommunity = session.session_type === 'community';
                      return (
                      <div 
                        key={session._id} 
                        className={`border rounded p-2 text-xs cursor-pointer hover:shadow-md transition ${
                          isCommunity
                            ? 'bg-orange-50 border-orange-300 hover:border-orange-500'
                            : 'bg-white border-gray-200 hover:border-blue-500'
                        }`}
                        onClick={() => navigate(`/admin/sessions/${session._id}`)}
                        data-testid={`session-${session._id}`}
                      >
                        <div className="flex items-start gap-1.5">
                          {isCommunity && (
                            <span
                              className="inline-flex items-center bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0"
                              data-testid={`community-badge-${session._id}`}
                            >
                              Community
                            </span>
                          )}
                          <div className="font-semibold text-gray-800 truncate flex-1">
                            {session.snapshot_name || session.class_name || '수업'}
                          </div>
                        </div>
                        <div className="flex items-center text-gray-600 mt-1">
                          <Clock className="h-3 w-3 mr-1" />
                          {new Date(session.scheduled_at).toLocaleTimeString('ko-KR', { 
                            timeZone: 'Asia/Seoul',
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                        <div className="flex items-center text-gray-600 mt-1">
                          <Users className="h-3 w-3 mr-1" />
                          {session.current_reservation_count} / {session.max_capacity}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* 수업 생성 모달 */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>수업 생성</DialogTitle>
          </DialogHeader>
          
          {/* 생성 방식 선택 */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={createMode === 'single' ? 'default' : 'outline'}
              onClick={() => { setCreateMode('single'); setBatchPreview(null); }}
              className="flex-1"
              data-testid="single-mode-btn"
            >
              단일 수업
            </Button>
            <Button
              variant={createMode === 'batch' ? 'default' : 'outline'}
              onClick={() => { setCreateMode('batch'); setBatchPreview(null); }}
              className="flex-1"
              data-testid="batch-mode-btn"
            >
              반복 수업
            </Button>
          </div>

          {createMode === 'single' ? (
            /* 단일 수업 폼 */
            <div className="space-y-4">
              <div>
                <Label>수업명 *</Label>
                <Input
                  value={singleForm.name}
                  onChange={(e) => setSingleForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="예: 필라테스 A반"
                  data-testid="single-name"
                />
              </div>
              <div>
                <Label>날짜 *</Label>
                <Input
                  type="date"
                  value={singleForm.scheduled_date}
                  onChange={(e) => setSingleForm(prev => ({ ...prev, scheduled_date: e.target.value }))}
                  data-testid="single-date"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>시작 시간 *</Label>
                  <Input
                    type="time"
                    value={singleForm.start_time}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      setSingleForm(prev => ({
                        ...prev,
                        start_time: newStart,
                        end_time: singleEndManual ? prev.end_time : calcEndTime(newStart)
                      }));
                    }}
                    data-testid="single-start-time"
                  />
                </div>
                <div>
                  <Label>종료 시간 *</Label>
                  <Input
                    type="time"
                    value={singleForm.end_time}
                    onChange={(e) => {
                      setSingleEndManual(true);
                      setSingleForm(prev => ({ ...prev, end_time: e.target.value }));
                    }}
                    data-testid="single-end-time"
                  />
                  <p className="text-xs text-gray-500 mt-1">자동 계산 (직접 수정 가능)</p>
                </div>
              </div>
              <div>
                <Label>정원 *</Label>
                <Input
                  type="number"
                  min="1"
                  value={singleForm.max_capacity}
                  onChange={(e) => setSingleForm(prev => ({ ...prev, max_capacity: parseInt(e.target.value) || 1 }))}
                  data-testid="single-capacity"
                />
              </div>
              <div>
                <Label>수업 유형 *</Label>
                <div className="flex gap-2 mt-1" data-testid="single-session-type">
                  <button
                    type="button"
                    onClick={() => setSingleForm(prev => ({ ...prev, session_type: 'general' }))}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                      singleForm.session_type === 'general'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="single-type-general"
                  >
                    일반 클래스
                  </button>
                  <button
                    type="button"
                    onClick={() => setSingleForm(prev => ({ ...prev, session_type: 'community' }))}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                      singleForm.session_type === 'community'
                        ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="single-type-community"
                  >
                    ⭐ 커뮤니티 클래스
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {singleForm.session_type === 'community'
                    ? '이벤트성 수업 — 14일 제한 없음, 이용권 차감 없음'
                    : '일반 수업 — 이용권 필요, 14일 이내만 예약 가능'}
                </p>
              </div>
              <div>
                <Label>코치 (선택)</Label>
                <Input
                  value={singleForm.instructor}
                  onChange={(e) => setSingleForm(prev => ({ ...prev, instructor: e.target.value }))}
                  placeholder="예: 김코치"
                  data-testid="single-instructor"
                />
              </div>
              <div>
                <Label>설명 (선택)</Label>
                <Textarea
                  value={singleForm.description}
                  onChange={(e) => setSingleForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="수업에 대한 설명"
                  rows={2}
                  data-testid="single-description"
                />
              </div>
            </div>
          ) : (
            /* 반복 수업 폼 */
            <div className="space-y-4">
              <div>
                <Label>수업명 *</Label>
                <Input
                  value={batchForm.name}
                  onChange={(e) => { setBatchForm(prev => ({ ...prev, name: e.target.value })); setBatchPreview(null); }}
                  placeholder="예: 요가 오전반"
                  data-testid="batch-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>시작일 *</Label>
                  <Input
                    type="date"
                    value={batchForm.start_date}
                    onChange={(e) => { setBatchForm(prev => ({ ...prev, start_date: e.target.value })); setBatchPreview(null); }}
                    data-testid="batch-start-date"
                  />
                </div>
                <div>
                  <Label>종료일 *</Label>
                  <Input
                    type="date"
                    value={batchForm.end_date}
                    onChange={(e) => { setBatchForm(prev => ({ ...prev, end_date: e.target.value })); setBatchPreview(null); }}
                    data-testid="batch-end-date"
                  />
                </div>
              </div>
              <div>
                <Label>반복 요일 *</Label>
                <div className="flex gap-2 mt-2">
                  {dayNames.map((name, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleDayOfWeek(idx)}
                      className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                        batchForm.days_of_week.includes(idx)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`day-${idx}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>시간 슬롯 *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 h-7"
                    onClick={() => {
                      setBatchTimeSlots(prev => [...prev, { start_time: '10:00', end_time: '10:50', endManual: false }]);
                      setBatchPreview(null);
                    }}
                    data-testid="add-time-slot"
                  >
                    + 시간 추가
                  </Button>
                </div>
                <div className="space-y-2">
                  {batchTimeSlots.map((slot, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={slot.start_time}
                        className="flex-1"
                        onChange={(e) => {
                          const newStart = e.target.value;
                          setBatchTimeSlots(prev => prev.map((s, i) => i === idx
                            ? { ...s, start_time: newStart, end_time: s.endManual ? s.end_time : calcEndTime(newStart) }
                            : s));
                          setBatchPreview(null);
                        }}
                        data-testid={`batch-start-time-${idx}`}
                      />
                      <span className="text-gray-400">~</span>
                      <Input
                        type="time"
                        value={slot.end_time}
                        className="flex-1"
                        onChange={(e) => {
                          setBatchTimeSlots(prev => prev.map((s, i) => i === idx
                            ? { ...s, end_time: e.target.value, endManual: true }
                            : s));
                          setBatchPreview(null);
                        }}
                        data-testid={`batch-end-time-${idx}`}
                      />
                      {batchTimeSlots.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600 h-8 w-8 p-0"
                          onClick={() => {
                            setBatchTimeSlots(prev => prev.filter((_, i) => i !== idx));
                            setBatchPreview(null);
                          }}
                          data-testid={`remove-slot-${idx}`}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">시작 시간 기준 50분 자동 계산 (직접 수정 가능)</p>
              </div>
              <div>
                <Label>정원 *</Label>
                <Input
                  type="number"
                  min="1"
                  value={batchForm.max_capacity}
                  onChange={(e) => setBatchForm(prev => ({ ...prev, max_capacity: parseInt(e.target.value) || 1 }))}
                  data-testid="batch-capacity"
                />
              </div>
              <div>
                <Label>수업 유형 *</Label>
                <div className="flex gap-2 mt-1" data-testid="batch-session-type">
                  <button
                    type="button"
                    onClick={() => setBatchForm(prev => ({ ...prev, session_type: 'general' }))}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                      batchForm.session_type === 'general'
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="batch-type-general"
                  >
                    일반 클래스
                  </button>
                  <button
                    type="button"
                    onClick={() => setBatchForm(prev => ({ ...prev, session_type: 'community' }))}
                    className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                      batchForm.session_type === 'community'
                        ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid="batch-type-community"
                  >
                    ⭐ 커뮤니티 클래스
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {batchForm.session_type === 'community'
                    ? '이벤트성 수업 — 14일 제한 없음, 이용권 차감 없음'
                    : '일반 수업 — 이용권 필요, 14일 이내만 예약 가능'}
                </p>
              </div>
              <div>
                <Label>코치 (선택)</Label>
                <Input
                  value={batchForm.instructor}
                  onChange={(e) => setBatchForm(prev => ({ ...prev, instructor: e.target.value }))}
                  placeholder="예: 박코치"
                  data-testid="batch-instructor"
                />
              </div>
              <div>
                <Label>설명 (선택)</Label>
                <Textarea
                  value={batchForm.description}
                  onChange={(e) => setBatchForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="수업에 대한 설명"
                  rows={2}
                  data-testid="batch-description"
                />
              </div>
              
              {/* 미리보기 버튼 */}
              <Button 
                type="button" 
                variant="outline" 
                onClick={previewBatchSessions}
                className="w-full"
                data-testid="preview-btn"
              >
                생성될 수업 수 확인
              </Button>
              
              {/* 미리보기 결과 */}
              {batchPreview && (
                <div className="p-4 bg-blue-50 rounded-lg text-sm" data-testid="batch-preview">
                  <p className="font-semibold text-blue-800">
                    총 {batchPreview.new_sessions}개의 수업이 생성됩니다
                  </p>
                  {batchPreview.duplicate_sessions > 0 && (
                    <p className="text-amber-600 mt-1">
                      (중복으로 {batchPreview.duplicate_sessions}개 건너뜀)
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              취소
            </Button>
            <Button
              onClick={createMode === 'single' ? createSingleSession : createBatchSessions}
              disabled={creating}
              data-testid="submit-btn"
            >
              {creating ? '생성 중...' : '생성'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Schedule;
